export const appTimeZone = process.env.REACT_APP_TIMEZONE || "Asia/Jakarta";

const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;

const partsToObject = (parts) =>
  parts.reduce((result, part) => {
    if (part.type !== "literal") result[part.type] = Number(part.value);
    return result;
  }, {});

const datePartsInAppTimezone = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return partsToObject(
    new Intl.DateTimeFormat("en-US", {
      timeZone: appTimeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date)
  );
};

const datePartsFromDateObject = (date) => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
});

const formatDateParts = ({ year, month, day }) =>
  String(year).padStart(4, "0") +
  "-" +
  String(month).padStart(2, "0") +
  "-" +
  String(day).padStart(2, "0");

export const currentBusinessDate = (now = new Date()) => {
  const parts = datePartsInAppTimezone(now);
  return parts ? formatDateParts(parts) : "";
};

export const dateOnlyFromValue = (value) => {
  if (value instanceof Date) return formatDateParts(datePartsFromDateObject(value));
  if (typeof value === "string" && dateOnlyPattern.test(value)) return value;
  return value ? currentBusinessDate(value) : currentBusinessDate();
};

export const addDaysToDateOnly = (value, amount) => {
  const [year, month, day] = dateOnlyFromValue(value).split("-").map(Number);
  const result = new Date(Date.UTC(year, month - 1, day + Number(amount || 0)));
  return formatDateParts({
    year: result.getUTCFullYear(),
    month: result.getUTCMonth() + 1,
    day: result.getUTCDate(),
  });
};

export const addMonthsToDateOnly = (value, amount) => {
  const [year, month, day] = dateOnlyFromValue(value).split("-").map(Number);
  const monthIndex = year * 12 + (month - 1) + Number(amount || 0);
  const targetYear = Math.floor(monthIndex / 12);
  const targetMonth = ((monthIndex % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  return formatDateParts({
    year: targetYear,
    month: targetMonth + 1,
    day: Math.min(day, lastDay),
  });
};

export const formatDateOnlyForLocale = (value, locale = "id-ID") => {
  const dateOnly = dateOnlyFromValue(value);
  const [year, month, day] = dateOnly.split("-").map(Number);
  return new Intl.DateTimeFormat(locale, {
    timeZone: "UTC",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(Date.UTC(year, month - 1, day)));
};

export const formatInstant = (value, { showTime = false, locale = "id-ID" } = {}) => {
  if (!value) return "-";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(locale, {
    timeZone: appTimeZone,
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(showTime
      ? { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }
      : {}),
  }).format(date);
};

export const formatDateValue = (value, options = {}) => {
  if (!value) return "-";
  if (typeof value === "string" && dateOnlyPattern.test(value)) {
    return formatDateOnlyForLocale(value, options.locale || "id-ID");
  }
  return formatInstant(value, options);
};
